import React, { useRef } from 'react';
import { FaUserPlus, FaComments, FaCode, FaCrown } from 'react-icons/fa';
import './BetaProcessSection.css';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { isIOS } from '../../hooks/isIos';

const FaUserPlusIcon = FaUserPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const FaCommentsIcon = FaComments as React.FC<React.SVGProps<SVGSVGElement>>;
const FaCodeIcon = FaCode as React.FC<React.SVGProps<SVGSVGElement>>;
const FaCrownIcon = FaCrown as React.FC<React.SVGProps<SVGSVGElement>>;

export const BetaProcessSection = () => {
	const ref = useRef(null);
	const marginValue = isIOS() ? '0px' : '-350px';
	const isInView = useInView(ref, { once: true, margin: marginValue });

	const processSteps = [
		{
			icon: <FaUserPlusIcon className='process-icon' />,
			step: '1',
			title: 'Beta-Anmeldung',
			description: 'Registriere dich für unseren exklusiven Beta-Test und werde Teil unserer Vision.',
			highlight: 'Kostenlos & Unverbindlich'
		},
		{
			icon: <FaCommentsIcon className='process-icon' />,
			step: '2',
			title: 'Interview-Einladung',
			description: 'Persönliches Gespräch über deine Bedürfnisse und Erwartungen an die perfekte Aufgabenverwaltung.',
			highlight: '15-30 Minuten'
		},
		{
			icon: <FaCodeIcon className='process-icon' />,
			step: '3',
			title: 'Beta-Entwicklung',
			description: 'Gemeinsame Entwicklung der ersten Beta-Version basierend auf deinem wertvollen Feedback.',
			highlight: 'Deine Ideen werden Realität'
		},
		{
			icon: <FaCrownIcon className='process-icon' />,
			step: '4',
			title: 'Premium-Belohnung',
			description: 'Lebenslanger Premium-Zugriff als Dankeschön für deine Unterstützung und dein Vertrauen.',
			highlight: 'Für immer kostenlos'
		},
	];

	return (
		<section className='beta-process-section' ref={ref}>
			<motion.div
				initial={{ opacity: 0, y: 50 }}
				animate={isInView ? { opacity: 1, y: 0 } : {}}
				transition={{ duration: 0.5, ease: 'easeOut' }}
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<h1 className='beta-process-title'>
					Dein Weg zum <span style={{ color: '#6C5CE7' }}>Beta-Tester:in</span>
				</h1>
				<p className='beta-process-description'>
					Werde Teil unserer exklusiven Beta-Community und gestalte die Zukunft von{' '}
					<strong>Erledix</strong> aktiv mit. Dein Feedback formt unser Produkt.
				</p>

				<div className='beta-process-timeline'>
					{processSteps.map((step, index) => (
						<motion.div
							className='process-step-container'
							key={index}
							initial={{ opacity: 0, y: 30 }}
							animate={isInView ? { opacity: 1, y: 0 } : {}}
							transition={{ delay: index * 0.1, duration: 0.4 }}
						>
							<div className='process-step-card'>
								<div className='process-step-header'>
									<div className='process-step-number'>{step.step}</div>
									{step.icon}
								</div>
								<h3 className='process-step-title'>{step.title}</h3>
								<p className='process-step-description'>{step.description}</p>
								<div className='process-step-highlight'>{step.highlight}</div>
							</div>

							{/* Connecting line/arrow (except for last step) */}
							{index < processSteps.length - 1 && (
								<motion.div
									className='process-connector'
									initial={{ scaleX: 0 }}
									animate={isInView ? { scaleX: 1 } : {}}
									transition={{ delay: (index + 1) * 0.1, duration: 0.3 }}
								/>
							)}
						</motion.div>
					))}
				</div>

				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={isInView ? { opacity: 1, y: 0 } : {}}
					transition={{ delay: 0.6, duration: 0.4 }}
					className='beta-process-cta'
				>
					<Link to='/beta-signup' className='cta-button' style={{ marginTop: '1rem' }}>
						🚀 Jetzt Beta-Tester:in werden
					</Link>
					<p className='beta-process-note'>
						Begrenzte Plätze verfügbar • Keine Verpflichtungen • 100% kostenlos
					</p>
				</motion.div>
			</motion.div>
		</section>
	);
};
